import { Link } from "react-router-dom";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";
import { useEffect, useState } from "react";
import { PublicDiscountsAPI, EventsAPI, PublicEventsAPI, MaintenanceAPI } from "../api";
import { getTopWeeklyCreators } from "../utils/topCreators";
import "./home.css";
import MaintenanceLanding from "./MaintenanceLanding";
import MaintenanceLandingExtended from "./MaintenanceLandingExtended";

import PointsRules from "../components/PointsRules";
import { resolveMediaUrl } from "../utils/url";

// Events: werden dynamisch geladen (für eingeloggte Nutzer)

const highlights = [
  { icon: "star",     color: "#8F5DD4", title: "Mentorenprogramm", desc: "Erfahrene Mitglieder helfen beim Einstieg & Wachstum." },
  { icon: "calendar", color: "#32CD89", title: "Events",           desc: "Exklusive Events, Challenges und Aktionen." },
  { icon: "user",     color: "#1C63D6", title: "Community",        desc: "Austausch, Support und Networking." },
  { icon: "trophy",   color: "#FFD700", title: "Punktesystem",     desc: "Engagement sammeln und Auszeichnungen erhalten." },
];

function Icon({ name, color = "#1C63D6", size = 32 }) {
  return (
    <svg width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      {name === "user" && (<g><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/></g>)}
      {/* Punkte-Regeln */}
      <section className="points-rules-section">
        <PointsRules />
      </section>
      {name === "star" && (<g><polygon points="12 2 15 8.5 22 9.3 17 14.1 18.5 21 12 17.8 5.5 21 7 14.1 2 9.3 9 8.5 12 2"/></g>)}
      {name === "calendar" && (<g><rect x="3" y="4" width="18" height="18" rx="4"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></g>)}
      {name === "trophy" && (<g><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10l-1 7a5 5 0 0 1-8 0L7 4z"/></g>)}
    </svg>
  );
}

export default function Home() {
  const { user } = useContext(UserContext);
  const [topCreators, setTopCreators] = useState([]);
  const [activeDeals, setActiveDeals] = useState([]);
  const [homeEvents, setHomeEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [maint, setMaint] = useState(null);
  useEffect(() => {
    getTopWeeklyCreators(3).then(setTopCreators);
    PublicDiscountsAPI.active().then(setActiveDeals).catch(() => setActiveDeals([]));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await MaintenanceAPI.status();
        if (!alive) return;
        setMaint(s || null);
      } catch {
        setMaint(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Lade Events für Startseite: eingeloggte Nutzer -> interne Liste; sonst -> öffentliche Liste
    async function loadEvents() {
      try {
        setEventsLoading(true);
        if (user) {
          const res = await EventsAPI.list({ pageSize: 50 });
          const now = new Date();
          const items = Array.isArray(res?.items) ? res.items : [];
          const upcoming = items.filter(ev => {
            const start = new Date(ev.startAt);
            const end = ev.endAt ? new Date(ev.endAt) : null;
            return end ? end >= now : start >= now;
          }).sort((a,b)=> new Date(a.startAt) - new Date(b.startAt));
          let picked = upcoming.slice(0, 2);
          if (picked.length < 2) {
            const past = items.filter(ev => {
              const start = new Date(ev.startAt);
              const end = ev.endAt ? new Date(ev.endAt) : null;
              return end ? end < now : start < now;
            }).sort((a,b)=> new Date(b.endAt || b.startAt) - new Date(a.endAt || a.startAt));
            picked = picked.concat(past.slice(0, 2 - picked.length));
          }
          setHomeEvents(picked);
        } else {
          const res = await PublicEventsAPI.list({ limit: 2, includePast: true });
          setHomeEvents((res?.items || []).slice(0,2));
        }
      } catch {
        setHomeEvents([]);
      } finally {
        setEventsLoading(false);
      }
    }
    loadEvents();
  }, [user]);

  // Render maintenance landing instead of homepage for non-privileged when active
  const isPrivileged = !!(user && ((Array.isArray(user.roles) && (user.roles.includes('admin') || user.roles.includes('owner'))) || user.role === 'admin' || user.role === 'owner'));
  if (maint && maint.enabled && !isPrivileged) {
    return maint.extendedMode ? (
      <MaintenanceLandingExtended status={maint} />
    ) : (
      <MaintenanceLanding status={maint} />
    );
  }

  // Derived promo info for active discounts
  const topDeals = [...activeDeals].sort((a,b) => Number(b?.percent||0) - Number(a?.percent||0));
  const maxPct = topDeals.length ? Math.max(...topDeals.map(d => Number(d?.percent || 0))) : 0;
  const titleLine = topDeals.length
    ? topDeals.slice(0, 2).map(d => d.name).join(' · ') + (activeDeals.length > 2 ? ` +${activeDeals.length - 2} mehr` : '')
    : 'Aktuelle Aktionen';
  const descLine = topDeals[0]?.description || '';
  // Earliest upcoming expiry across active deals
  const futureValidTos = activeDeals
    .map(d => (d.validTo ? new Date(d.validTo) : null))
    .filter(dt => dt && dt.getTime() >= Date.now());
  const nearestValidTo = futureValidTos.length ? new Date(Math.min(...futureValidTos.map(d => d.getTime()))) : null;
  const expiryText = nearestValidTo ? nearestValidTo.toLocaleDateString('de-DE') : '';

  return (
  <div className="home-page" style={{width:'100vw',margin:0,padding:0}}>
      {/* HERO – full-bleed */}
      <section className="home-hero">
        <svg className="hero-bg-svg" viewBox="0 0 1440 400" aria-hidden="true" >
          <circle cx="200" cy="120" r="4" fill="#1C63D6" />
          <circle cx="400" cy="80"  r="3" fill="#8F5DD4" />
          <circle cx="900" cy="200"  r="5" fill="#32CD89" />
          <circle cx="1200" cy="100" r="4" fill="#FFD700" />
          <line x1="200" y1="120" x2="400"  y2="80"  stroke="#1C63D6" strokeWidth="2" />
          <line x1="400" y1="80"  x2="900"  y2="200" stroke="#8F5DD4" strokeWidth="2" />
          <line x1="900" y1="200" x2="1200" y2="100" stroke="#32CD89" strokeWidth="2" />
        </svg>

        <div className="hero-content" style={{width:'100vw',margin:0,padding:0}}>
          <div className="hero-logo-bar" aria-hidden="true">
            {/* passe das Logo an – Datei in /public ablegen */}
            <img className="hero-logo-img" src="/apn-3d-logo.png" alt="AP-N Logo" />
            {/* <img className="hero-logo-img" src="/apn-logo-2.png" alt="AP-N Logo" /> */}
          </div>

          <h1 className="hero-title">Gemeinsam. Sichtbar. Stark.</h1>
          <p className="hero-desc">
            Die exklusive Community für ambitionierte Creator. <br/>
            Qualität, Teamgeist und gegenseitige Unterstützung <br/>
            stehen hier im Mittelpunkt.
          </p>

          <div className="hero-btn-row">
            {!user ? (
              <>
                <Link className="hero-btn-primary" to="/register">Jetzt beitreten</Link>
                <Link className="hero-btn-secondary" to="/login">Login</Link>
              </>
            ) : (
              <>
                <Link className="hero-btn-primary" to="/dashboard">Zum Dashboard</Link>
                {user.role === "admin" && (
                  <Link className="hero-btn-secondary" to="/admin/users">Benutzerverwaltung</Link>
                )}
              </>
            )}
          </div>
        </div>

        <div className="hero-float1" />
        <div className="hero-float2" />
        <div className="hero-float3" />
      </section>

      {/* HIGHLIGHTS */}
      <section className="highlights-section">
        {activeDeals.length > 0 && (
          <div style={{width:'100%',maxWidth:'1200px',margin:'0 auto',padding:'0 0 12px 0'}}>
            <div style={{
              display:'flex',
              alignItems:'center',
              gap:16,
              padding:'14px 16px',
              border:'1px solid rgba(34,197,94,0.55)',
              background:'linear-gradient(90deg, rgba(34,197,94,0.22), rgba(34,197,94,0.12))',
              borderRadius:12,
              boxShadow:'0 8px 24px rgba(34,197,94,0.25)',
              color:'#f0fff0'
            }}>
              <span style={{fontSize:24}}>🏷️</span>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:900, letterSpacing:0.3, fontSize:18, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                  {titleLine}
                </div>
                {descLine && (
                  <div style={{fontSize:14, color:'#e6ffe6', marginTop:2, whiteSpace:'normal'}}>
                    {descLine}
                  </div>
                )}
                {expiryText && (
                  <div style={{fontSize:12, color:'#d7f5d7', marginTop:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    ⏳ Nur gültig bis {expiryText}
                  </div>
                )}
              </div>
              {maxPct > 0 && (
                <span title={`Spare bis zu ${Math.round(maxPct)}%`} style={{
                  fontSize:13,
                  color:'#083d1a',
                  background:'#9FE870',
                  border:'1px solid rgba(34,197,94,0.6)',
                  padding:'6px 10px',
                  borderRadius:9999,
                  fontWeight:900,
                  whiteSpace:'nowrap'
                }}>Spare bis zu {Math.round(maxPct)}%</span>
              )}
              <Link to="/angebote" className="hero-btn-primary" style={{whiteSpace:'nowrap', marginLeft:8}}>Jetzt sparen</Link>
            </div>
          </div>
        )}
        <div style={{width:'100%',maxWidth:'1200px',margin:'0 auto',overflowX:'auto',padding:0}}>
          <div className="highlights-grid">
            {highlights.map((h, i) => (
              <div className="highlight-card" key={i}>
                <Icon name={h.icon} color={h.color} size={48} />
                <h2 className="highlight-title">{h.title}</h2>
                <p className="highlight-desc">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOP-CREATORS & EVENTS */}
      <section className="creators-events-section">
        <div style={{width:'100%',maxWidth:'1200px',margin:'0 auto',padding:0}}>
          <div className="creators-events-grid">
            <div>
              <h3 className="section-subtitle">Unsere Top-Creator der Woche</h3>
              <div className="creators-list">
                {topCreators.map((c, i) => (
                  <Link key={c.id} to={`/u/${c.id}`} className="creator-card" style={{textDecoration:'none'}}>
                    <div className="avatar-wrap">
                      <img src={c.avatar} alt={c.name} onError={(e)=>{e.currentTarget.style.display="none"; e.currentTarget.parentElement.classList.add("avatar-fallback");}}/>
                    </div>
                    <span className="creator-name">{c.name}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h3 className="section-subtitle">Aktuelle Events</h3>
              <div className="events-list">
                {eventsLoading ? (
                  <div style={{ color:'#9ca3af', fontSize:13 }}>Lade…</div>
                ) : (homeEvents && homeEvents.length > 0) ? (
                  homeEvents.slice(0,2).map(ev => (
                    <Link key={ev.id} to={`/events/${ev.id}`} className="event-card" style={{ textDecoration:'none' }}>
                      <div className="event-thumb">
                        {ev.posterUrl ? (
                          <img src={resolveMediaUrl(ev.posterUrl)} alt={ev.title} onError={(e)=>{e.currentTarget.style.display='none'; e.currentTarget.parentElement.classList.add('event-thumb-fallback');}} />
                        ) : (
                          <div className="event-thumb-fallback" />
                        )}
                      </div>
                      <span className="event-title">{ev.title}</span>
                      <span className="event-date">{formatDateRange(ev.startAt, ev.endAt)}</span>
                    </Link>
                  ))
                ) : (
                  <div style={{ color:'#9ca3af', fontSize:13 }}>Keine anstehenden Events.</div>
                )}
              </div>
              <div style={{ marginTop: 8, display:'flex', justifyContent:'flex-end' }}>
                {user ? (
                  <Link to="/events" className="hero-btn-secondary" style={{ padding:'8px 12px' }}>Weitere Events</Link>
                ) : (
                  <Link to="/login?redirect=%2Fevents" className="hero-btn-secondary" style={{ padding:'8px 12px' }}>Weitere Events</Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div style={{width:'100%',margin:0,padding:0}}>
          <h2 className="cta-title">Werde Teil des AP-Network</h2>
          <p className="cta-desc">Registriere dich jetzt und profitiere von exklusiven Vorteilen.</p>
          {!user ? (
            <Link to="/register" className="cta-btn">Jetzt registrieren</Link>
          ) : (
            <Link to="/dashboard" className="cta-btn">Zum Dashboard</Link>
          )}
        </div>
      </section>
    </div>
  );
}

function formatDateRange(startAt, endAt) {
  try {
    const s = new Date(startAt);
    const e = endAt ? new Date(endAt) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const sStr = new Intl.DateTimeFormat('de-DE', opts).format(s);
    const eStr = e ? new Intl.DateTimeFormat('de-DE', opts).format(e) : null;
    return eStr ? `${sStr} – ${eStr}` : sStr;
  } catch { return String(startAt); }
}
